@file:Suppress("MagicNumber", "TooManyFunctions")

package com.nonopn.skydome.common.app.pages.promocodes

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.Icon
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.rounded.Add
import androidx.compose.material.icons.rounded.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.api.impl.AdminPromotionCode
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.home.LocalApp
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.promo_action_delete
import com.nonopn.skywatcher.common.app.res.promo_action_disable
import com.nonopn.skywatcher.common.app.res.promo_action_edit
import com.nonopn.skywatcher.common.app.res.promo_action_enable
import com.nonopn.skywatcher.common.app.res.promo_action_error
import com.nonopn.skywatcher.common.app.res.promo_referrer_locked
import com.nonopn.skywatcher.common.app.res.promo_admin_count
import com.nonopn.skywatcher.common.app.res.promo_admin_empty
import com.nonopn.skywatcher.common.app.res.promo_admin_load_error
import com.nonopn.skywatcher.common.app.res.promo_admin_new
import com.nonopn.skywatcher.common.app.res.promo_admin_no_match
import com.nonopn.skywatcher.common.app.res.promo_admin_search_hint
import com.nonopn.skywatcher.common.app.res.promo_cancel
import com.nonopn.skywatcher.common.app.res.promo_commission_line
import com.nonopn.skywatcher.common.app.res.promo_delete_body_unused
import com.nonopn.skywatcher.common.app.res.promo_delete_body_used
import com.nonopn.skywatcher.common.app.res.promo_delete_body_used_disabled
import com.nonopn.skywatcher.common.app.res.promo_delete_confirm
import com.nonopn.skywatcher.common.app.res.promo_delete_confirm_anyway
import com.nonopn.skywatcher.common.app.res.promo_delete_disable_instead
import com.nonopn.skywatcher.common.app.res.promo_delete_title
import com.nonopn.skywatcher.common.app.res.promo_filter_all
import com.nonopn.skywatcher.common.app.res.promo_filter_app_all
import com.nonopn.skywatcher.common.app.res.promo_referrer
import com.nonopn.skywatcher.common.app.res.promo_referrer_commission
import com.nonopn.skywatcher.common.app.res.promo_retry
import com.nonopn.skywatcher.common.app.res.promo_state_active
import com.nonopn.skywatcher.common.app.res.promo_state_disabled
import com.nonopn.skywatcher.common.app.res.promo_state_exhausted
import com.nonopn.skywatcher.common.app.res.promo_state_expired
import com.nonopn.skywatcher.common.app.res.promo_stripe_badge
import com.nonopn.skywatcher.common.app.res.promo_toast_deleted
import com.nonopn.skywatcher.common.app.res.promo_toast_disabled
import com.nonopn.skywatcher.common.app.res.promo_toast_enabled
import com.nonopn.skywatcher.common.app.res.promo_undo
import com.nonopn.skywatcher.common.app.res.promo_uses
import com.nonopn.skywatcher.common.app.res.promo_uses_unlimited
import com.nonopn.skywatcher.common.app.res.promo_valid_forever
import com.nonopn.skywatcher.common.app.res.promo_valid_until
import com.nonopn.skywatcher.design.theme.AppColor
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.viewmodel.rememberViewModel
import org.jetbrains.compose.resources.stringResource

private val Ink = Color(0xFF1A2433)
private val Muted = Color(0xFF5B6678)
private val Chip = Color(0xFFE8EDF5)
private val Danger = Color(0xFFE63B2E)
private val Green = Color(0xFF2E9E5B)
private val Orange = Color(0xFFF28C28)

/** Administrator list of the promotion codes (mock-up of 2026-11-02, section 2). */
@Composable
fun PromoCodesAdminScreen(
    modifier: Modifier,
    onEdit: (uuid: String) -> Unit,
    onCreate: () -> Unit,
) {
    val appModel: AppModel = LocalApp.current
    val model = rememberViewModel { PromoCodesAdminModel(appModel) }
    val state by model.states.collectAsState()
    val codes by PromoCodesAdminStore.codes.collectAsState()
    val appState by appModel.states.collectAsState()
    val language = appState.appLanguage
    val now = promoNowSeconds()
    val offset = promoLocalOffsetSeconds()

    val ofApp = codes.filter { state.appFilter.app == null || it.app == state.appFilter.app }
    val withStates = ofApp.map { it to it.resolvedState(now) }
    val counts = promoStateCounts(withStates.map { it.second })
    val visible = withStates
        .filter { (code, codeState) ->
            state.stateFilter.accepts(codeState) && matchesPromoQuery(code.code, code.referrer?.email, state.query)
        }
        .sortedByDescending { it.first.createdAtSecondsUtc }

    Box(modifier = modifier.background(AppColor.StrikeBackground)) {
        Column(Modifier.fillMaxSize()) {
            PromoFilterBar(state, counts, visible.size, ofApp.size, model)
            when {
                state.loading && codes.isEmpty() -> Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = AppColor.StrikePrimary)
                }
                state.loadFailed && codes.isEmpty() -> Column(
                    Modifier.fillMaxSize().padding(24.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    TextNormal(stringResource(Res.string.promo_admin_load_error), color = AppColor.StrikeText)
                    TextButton(onClick = { model.load() }) {
                        TextNormal(stringResource(Res.string.promo_retry), color = AppColor.StrikePrimary)
                    }
                }
                visible.isEmpty() -> Box(Modifier.fillMaxSize().padding(24.dp), contentAlignment = Alignment.Center) {
                    TextNormal(
                        stringResource(if (codes.isEmpty()) Res.string.promo_admin_empty else Res.string.promo_admin_no_match),
                        color = AppColor.StrikeText
                    )
                }
                else -> LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    contentPadding = androidx.compose.foundation.layout.PaddingValues(
                        start = 12.dp,
                        end = 12.dp,
                        top = 4.dp,
                        bottom = 96.dp
                    )
                ) {
                    items(visible, key = { it.first.uuid }) { (code, codeState) ->
                        PromoCodeCard(
                            code = code,
                            codeState = codeState,
                            language = language,
                            offsetSeconds = offset,
                            busy = state.busyUuid == code.uuid,
                            onEdit = { onEdit(code.uuid) },
                            onToggle = { model.setActive(code, !code.active) },
                            onDelete = { model.requestDelete(code) },
                        )
                    }
                }
            }
        }

        Column(
            modifier = Modifier.align(Alignment.BottomCenter).fillMaxWidth().padding(12.dp),
            horizontalAlignment = Alignment.End,
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Row(
                modifier = Modifier
                    .clip(RoundedCornerShape(24.dp))
                    .background(AppColor.StrikePrimary)
                    .clickable(onClick = onCreate)
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(Icons.Rounded.Add, contentDescription = null, tint = Color.White)
                Spacer(Modifier.width(6.dp))
                TextNormal(
                    text = stringResource(Res.string.promo_admin_new),
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
            }
            NoticeBar(state, model)
        }

        state.pendingDelete?.let { DeleteDialog(it, model) }
    }
}

@Composable
private fun PromoFilterBar(
    state: PromoCodesAdminState,
    counts: Map<PromoStateFilter, Int>,
    shown: Int,
    total: Int,
    model: PromoCodesAdminModel,
) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 8.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            value = state.query,
            onValueChange = { model.setQuery(it) },
            placeholder = {
                TextNormal(stringResource(Res.string.promo_admin_search_hint), fontSize = 13.sp, color = Muted)
            },
            leadingIcon = { Icon(imageVector = Icons.Rounded.Search, contentDescription = null, tint = Muted) },
            singleLine = true,
            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
            shape = RoundedCornerShape(10.dp),
            colors = OutlinedTextFieldDefaults.colors(
                focusedContainerColor = Color.White,
                unfocusedContainerColor = Color.White,
                focusedTextColor = Ink,
                unfocusedTextColor = Ink,
                cursorColor = Ink,
                focusedBorderColor = AppColor.StrikePrimary,
                unfocusedBorderColor = Color(0xFF8A94A6)
            )
        )
        Row(
            modifier = Modifier.horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            PromoStateFilter.entries.forEach { filter ->
                FilterChip(
                    label = "${stateFilterLabel(filter)} ${counts[filter] ?: 0}",
                    selected = state.stateFilter == filter,
                    onClick = { model.setStateFilter(filter) }
                )
            }
        }
        Row(
            modifier = Modifier.horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            PromoAppFilter.entries.forEach { filter ->
                FilterChip(
                    label = filter.app ?: stringResource(Res.string.promo_filter_app_all),
                    selected = state.appFilter == filter,
                    onClick = { model.setAppFilter(filter) }
                )
            }
        }
        Box(
            modifier = Modifier.clip(RoundedCornerShape(10.dp)).background(Chip).padding(horizontal = 10.dp, vertical = 3.dp)
        ) {
            TextNormal(
                stringResource(Res.string.promo_admin_count, shown, total),
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                color = Ink
            )
        }
    }
}

@Composable
private fun stateFilterLabel(filter: PromoStateFilter): String = when (filter) {
    PromoStateFilter.All -> stringResource(Res.string.promo_filter_all)
    PromoStateFilter.Active -> stringResource(Res.string.promo_state_active)
    PromoStateFilter.Disabled -> stringResource(Res.string.promo_state_disabled)
    PromoStateFilter.Expired -> stringResource(Res.string.promo_state_expired)
    PromoStateFilter.Exhausted -> stringResource(Res.string.promo_state_exhausted)
}

@Composable
internal fun promoStateLabel(state: PromoCodeState): String = when (state) {
    PromoCodeState.Active -> stringResource(Res.string.promo_state_active)
    PromoCodeState.Disabled -> stringResource(Res.string.promo_state_disabled)
    PromoCodeState.Expired -> stringResource(Res.string.promo_state_expired)
    PromoCodeState.Exhausted -> stringResource(Res.string.promo_state_exhausted)
}

private fun stateColor(state: PromoCodeState): Color = when (state) {
    PromoCodeState.Active -> Green
    PromoCodeState.Disabled -> Muted
    PromoCodeState.Expired -> Danger
    PromoCodeState.Exhausted -> Orange
}

@Composable
private fun FilterChip(label: String, selected: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(16.dp))
            .background(if (selected) AppColor.StrikePrimary else Chip)
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 5.dp),
    ) {
        TextNormal(
            text = label,
            fontSize = 11.sp,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
            color = if (selected) Color.White else Ink
        )
    }
}

@Composable
private fun Badge(text: String, color: Color) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(color.copy(alpha = 0.15f))
            .padding(horizontal = 8.dp, vertical = 2.dp)
    ) {
        TextNormal(text, fontSize = 11.sp, fontWeight = FontWeight.SemiBold, color = color)
    }
}

@Suppress("LongParameterList", "LongMethod")
@Composable
private fun PromoCodeCard(
    code: AdminPromotionCode,
    codeState: PromoCodeState,
    language: String,
    offsetSeconds: Long,
    busy: Boolean,
    onEdit: () -> Unit,
    onToggle: () -> Unit,
    onDelete: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(Color.White)
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            TextNormal(code.code, fontSize = 15.sp, fontWeight = FontWeight.Bold, color = Ink)
            Spacer(Modifier.width(8.dp))
            Badge(promoStateLabel(codeState), stateColor(codeState))
            Spacer(Modifier.width(6.dp))
            TextNormal(code.app, fontSize = 11.sp, color = Muted)
            if (code.onStripeCheckout) {
                Spacer(Modifier.width(6.dp))
                Badge(stringResource(Res.string.promo_stripe_badge), AppColor.StrikePrimary)
            }
        }
        TextNormal(promoBenefitLabel(code.benefit()), fontSize = 13.sp, color = Ink)
        val uses = code.maxRedemptions?.let { stringResource(Res.string.promo_uses, code.redemptions, it) }
            ?: stringResource(Res.string.promo_uses_unlimited, code.redemptions)
        val validity = code.validUntilSecondsUtc
            ?.let { stringResource(Res.string.promo_valid_until, formatPromoEndDate(it, offsetSeconds)) }
            ?: stringResource(Res.string.promo_valid_forever)
        TextNormal("$uses · $validity", fontSize = 12.sp, color = Muted)
        code.referrer?.email?.takeIf { it.isNotBlank() }?.let { email ->
            val commission = code.commissionPercent
                ?.let { " · " + stringResource(Res.string.promo_referrer_commission, it) }
                .orEmpty()
            TextNormal(stringResource(Res.string.promo_referrer, email) + commission, fontSize = 12.sp, color = Ink)
        }
        // One line per currency: amounts of different currencies are never added
        promoCommissionLines(code.commissionTotals(), language).forEach { line ->
            TextNormal(
                stringResource(Res.string.promo_commission_line, line.commission, line.paid, line.invoices),
                fontSize = 12.sp,
                color = Ink
            )
        }
        if (code.referrerLocked()) {
            TextNormal(stringResource(Res.string.promo_referrer_locked), fontSize = 12.sp, color = Danger)
        }
        Spacer(Modifier.height(4.dp))
        val actions = promoRowActions(code.active)
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            if (busy) {
                CircularProgressIndicator(modifier = Modifier.width(20.dp).height(20.dp), color = AppColor.StrikePrimary)
            }
            val locked = code.referrerLocked()
            ActionText(stringResource(Res.string.promo_action_edit), AppColor.StrikePrimary, !busy && !locked, onEdit)
            Spacer(Modifier.weight(1f))
            // Main action: disable (reversible) or enable
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .background(
                        if (busy || locked) AppColor.StrikePrimary.copy(alpha = 0.35f) else AppColor.StrikePrimary
                    )
                    .clickable(enabled = !busy && !locked, onClick = onToggle)
                    .padding(horizontal = 12.dp, vertical = 6.dp)
            ) {
                TextNormal(
                    stringResource(
                        if (actions.primary == PromoRowAction.Enable) {
                            Res.string.promo_action_enable
                        } else {
                            Res.string.promo_action_disable
                        }
                    ),
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
            }
            if (PromoRowAction.Delete in actions.secondary) {
                ActionText(stringResource(Res.string.promo_action_delete), Danger, !busy, onDelete)
            }
        }
    }
}

@Composable
private fun ActionText(text: String, color: Color, enabled: Boolean, onClick: () -> Unit) {
    TextNormal(
        text,
        modifier = Modifier.clickable(enabled = enabled, onClick = onClick).padding(horizontal = 4.dp, vertical = 6.dp),
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        color = if (enabled) color else color.copy(alpha = 0.4f)
    )
}

@Composable
private fun NoticeBar(state: PromoCodesAdminState, model: PromoCodesAdminModel) {
    val notice = state.notice
    val text = when {
        state.actionFailed -> stringResource(Res.string.promo_action_error)
        notice == null -> return
        notice.kind == PromoNotice.Kind.Disabled -> stringResource(Res.string.promo_toast_disabled, notice.code.code)
        notice.kind == PromoNotice.Kind.Enabled -> stringResource(Res.string.promo_toast_enabled, notice.code.code)
        else -> stringResource(Res.string.promo_toast_deleted, notice.code.code)
    }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(Ink)
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TextNormal(text, modifier = Modifier.weight(1f), fontSize = 13.sp, color = Color.White)
        if (!state.actionFailed && notice != null && notice.kind != PromoNotice.Kind.Deleted) {
            TextNormal(
                stringResource(Res.string.promo_undo),
                modifier = Modifier.clickable { model.undo() }.padding(6.dp),
                fontSize = 13.sp,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF8CC8FF)
            )
        }
        TextNormal("✕", modifier = Modifier.clickable { model.dismissNotice() }.padding(6.dp), color = Color.White)
    }
}

/**
 * Confirmation of the deletion (irreversible). Unused code: simple confirmation. Used and active code: "Disable
 * instead" is the main button, "Delete anyway" stays available. Used and already disabled: explicit confirmation.
 */
@Composable
private fun DeleteDialog(code: AdminPromotionCode, model: PromoCodesAdminModel) {
    val confirmation = promoDeleteConfirmation(code.active, code.redemptions)
    AlertDialog(
        onDismissRequest = { model.cancelDelete() },
        title = { TextNormal(stringResource(Res.string.promo_delete_title, code.code), fontWeight = FontWeight.Bold) },
        text = {
            TextNormal(
                when (confirmation) {
                    PromoDeleteConfirmation.Simple -> stringResource(Res.string.promo_delete_body_unused)
                    PromoDeleteConfirmation.SuggestDisable ->
                        stringResource(Res.string.promo_delete_body_used, code.redemptions)
                    PromoDeleteConfirmation.UsedAlreadyDisabled ->
                        stringResource(Res.string.promo_delete_body_used_disabled, code.redemptions)
                }
            )
        },
        confirmButton = {
            if (confirmation == PromoDeleteConfirmation.SuggestDisable) {
                Column(horizontalAlignment = Alignment.End) {
                    TextButton(onClick = { model.disableInstead() }) {
                        TextNormal(
                            stringResource(Res.string.promo_delete_disable_instead),
                            color = AppColor.StrikePrimary,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    TextButton(onClick = { model.confirmDelete() }) {
                        TextNormal(stringResource(Res.string.promo_delete_confirm_anyway), color = Danger)
                    }
                }
            } else {
                TextButton(onClick = { model.confirmDelete() }) {
                    TextNormal(stringResource(Res.string.promo_delete_confirm), color = Danger, fontWeight = FontWeight.Bold)
                }
            }
        },
        dismissButton = {
            TextButton(onClick = { model.cancelDelete() }) { TextNormal(stringResource(Res.string.promo_cancel)) }
        }
    )
}
